<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  label: { type: String, required: true },
  options: { type: Array, default: () => [] },
  selected: { type: Array, default: () => [] },
  filterKey: { type: String, required: true },
  isMobile: { type: Boolean, default: false },
  filterShowUrl: { type: Boolean, default: false },
  getLinkFn: { type: Function, required: true },
});

const emit = defineEmits(['change', 'selectAll']);

const MAX_VISIBLE_OPTIONS = 10;

const filterOpen = ref({
  [props.filterKey]: !props.isMobile,
});

const showAll = ref({
  [props.filterKey]: false,
});

function toggleFilterOpen(key) {
  filterOpen.value[key] = !filterOpen.value[key];
}

function toggleShowAll(key) {
  showAll.value[key] = !showAll.value[key];
}

const itemModel = computed({
  get: () => props.selected,
  set: (val) => emit('change', val),
});

function toggleChip(slug) {
  const current = [...props.selected];
  const index = current.indexOf(slug);
  if (index > -1) {
    current.splice(index, 1);
  } else {
    current.push(slug);
  }
  emit('change', current);
}

const allSelected = computed(() => {
  return props.selected.length === 0;
});

function selectAll() {
  emit('change', []);
}

const visibleOptions = computed(() => {
  if (
    props.isMobile ||
    props.filterKey === 'region' ||
    showAll.value[props.filterKey]
  ) {
    return props.options;
  }
  return props.options.slice(0, MAX_VISIBLE_OPTIONS);
});

function getFilterLink(key, slug) {
  return `/${key}/${slug}`;
}
</script>

<template>
  <div
    v-if="options?.length"
    class="catalog__filter-container"
    :class="{ 'is-mobile': isMobile }"
  >
    <div class="catalog__filter-top" :class="{ 'mobile-header': isMobile }">
      <div
        class="catalog__filter-header"
        :class="{ 'catalog__filter-header--expanded': filterOpen[filterKey] }"
        @click="toggleFilterOpen(filterKey)"
      >
        <p class="mb-0 catalog__filter-title h6-mobile">
          {{ label }}
          <span v-if="isMobile && selected.length" class="section-count">{{
            selected.length
          }}</span>
        </p>
        <button
          type="button"
          class=""
          :class="[
            'catalog__filter-toggle',
            `js-button-filter-${filterKey}-toggle`,
          ]"
        >
          <CIcon
            :class="[
              'catalog__filter-toggle-icon',
              filterOpen[filterKey]
                ? 'catalog__filter-toggle-icon--rotated'
                : '',
            ]"
            name="direction-black"
            width="16"
            height="16"
          />
        </button>
      </div>
    </div>

    <div v-if="filterOpen[filterKey]" class="catalog__filter-content">
      <div v-if="isMobile" class="option-chips">
        <button
          v-for="opt in options"
          :key="`${filterKey}_mobile_${opt.id}`"
          class="chip"
          :class="[
            { selected: selected.includes(opt.slug) },
            `js-button-filter-mobile-${opt.id}`,
          ]"
          @click="toggleChip(opt.slug)"
        >
          <span v-html="opt.title"></span>
        </button>
      </div>

      <div v-else class="catalog__filter-group">
        <div class="catalog__filter-options">
          <div class="catalog__filter-option">
            <label class="catalog__filter-checkbox checkbox">
              <Checkbox
                :binary="true"
                :model-value="allSelected"
                class="checkbox-input"
                @update:model-value="selectAll"
              >
                <template #icon>
                  <CIcon
                    name="check-white"
                    width="12"
                    height="12"
                    class="checkbox-icon"
                  />
                </template>
              </Checkbox>
              <span class="checkbox-label body-3-regular">{{
                $t('filters.all')
              }}</span>
            </label>
          </div>

          <div
            v-for="opt in visibleOptions"
            :key="`${filterKey}_${opt.id}`"
            class="catalog__filter-option"
          >
            <label class="catalog__filter-checkbox checkbox">
              <Checkbox
                v-model="itemModel"
                :input-id="`${filterKey}-${opt.id}`"
                :value="opt.slug"
                class="checkbox-input"
              >
                <template #icon>
                  <CIcon
                    name="check-white"
                    width="12"
                    height="12"
                    class="checkbox-icon"
                  />
                </template>
              </Checkbox>
              <span
                class="checkbox-label body-3-regular"
                v-html="opt.title"
              ></span>
            </label>
            <NuxtLink
              v-if="filterShowUrl"
              :to="getLinkFn(filterKey, opt.slug)"
              class="catalog__filter-link"
              aria-hidden="true"
              tabindex="-1"
              hidden
            >
              {{ opt.title }}
            </NuxtLink>
          </div>
        </div>

        <div
          v-if="filterKey !== 'region' && options.length > MAX_VISIBLE_OPTIONS"
          class="catalog__filter-show"
        >
          <Button
            variant="text"
            size="small"
            :class="[
              'catalog__filter-show-button',
              'link-normal',
              `js-button-filter-${filterKey}`,
            ]"
            @click.stop="toggleShowAll(filterKey)"
          >
            <span>{{
              showAll[filterKey]
                ? $t('filters.show_less')
                : $t('filters.show_more')
            }}</span>
            <CIcon
              name="direction-small-black"
              width="16"
              height="16"
              :class="[
                'catalog__filter-show-arrow',
                { 'catalog__filter-show-arrow--up': showAll[filterKey] },
              ]"
            />
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.catalog__filter {
  &-top {
    background-color: $white;
    padding: 24px 16px 12px;
    position: sticky;
    top: 65px;
    z-index: 1;
    width: 100%;
    @include media-breakpoint-up(md) {
      top: 58px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-top-width, 1);
      @include responsive-spacing(padding-bottom, 12);
      @include responsive-spacing(padding-top, 24);
      @include responsive-spacing(padding-left, 16);
      @include responsive-spacing(padding-right, 16);
      @include responsive-spacing(top, 58);
    }
  }
  &-header {
    align-items: center;
    cursor: pointer;
    display: grid;
    justify-content: space-between;
    grid-template-columns: auto 16px;
    padding: 0 8px;
    @include media-breakpoint-up(xl) {
      grid-template-columns: auto px-to-vw(16);
      @include responsive-spacing(padding-left, 8);
      @include responsive-spacing(padding-right, 8);
    }
  }
  &-title {
    color: $black;
  }
  &-link {
    display: none;
  }
  &-toggle {
    cursor: pointer;
    padding: 0;
    &-icon {
      height: auto;
      transform: rotate(90deg);
      transition: transform 0.3s ease;
      width: 100%;
      &--rotated {
        transform: rotate(-90deg);
      }
    }
  }
  &-content {
    border-bottom: 1px solid $divider;
    padding: 12px 16px 16px;
    width: 100%;
    @include media-breakpoint-up(md) {
      &:last-child {
        border-bottom: unset;
      }
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-bottom-width, 1);
      @include responsive-spacing(padding-bottom, 16);
      @include responsive-spacing(padding-top, 12);
      @include responsive-spacing(padding-left, 16);
      @include responsive-spacing(padding-right, 16);
    }
  }
  &-group {
    display: flex;
    flex-direction: column;
    gap: 16px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
  }
  &-options {
    display: flex;
    flex-direction: column;
    gap: 4px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 4);
    }
  }
  &-option {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 32px;
    @include media-breakpoint-up(lg) {
      padding: 6px 4px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 12);
      @include responsive-spacing(padding-bottom, 6);
      @include responsive-spacing(padding-top, 6);
      @include responsive-spacing(padding-left, 4);
      @include responsive-spacing(padding-right, 4);
      @include responsive-spacing(min-height, 32);
    }
  }
  &-checkbox {
    flex-shrink: 0;
    width: 100%;
  }
  &-show {
    &-button {
      align-items: center;
      display: grid;
      justify-content: start;
      gap: 8px;
      grid-template-columns: auto 16px;
      padding: 0 12px !important;
      @include media-breakpoint-up(md) {
        padding: 0 4px !important;
      }
      @include media-breakpoint-up(xl) {
        grid-template-columns: auto px-to-vw(16);
        @include responsive-spacing(gap, 8);
        @include responsive-spacing(padding-left, 4, true);
        @include responsive-spacing(padding-right, 4, true);
      }
    }
    &-arrow {
      height: auto;
      transform: rotate(90deg);
      width: 100%;
      &--up {
        transform: rotate(-90deg);
      }
    }
  }
}
</style>
